import { CSSProperties, useRef, useState } from "react";
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  limitShift,
  offset,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
} from "@floating-ui/react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { P1B } from "./TextComponents/P1B";
import { P2 } from "./TextComponents/P2";
import { cn } from "../ClassName";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

type VariantType = "light" | "dark";

type Placement =
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end";
interface Props {
  header?: string;
  text: string | React.ReactNode;
  closeFunction?: () => void;
  children?: any;
  className?: string;
  style?: CSSProperties;
  closeable?: boolean;
  variant?: VariantType;
  hoverTooltip?: boolean;
  clickTooltip?: boolean;
  /**
   * The strategy to use for positioning the tooltip.
   * - `fixed` will position the tooltip relative to the viewport.
   * - `absolute` will position the tooltip relative to the nearest positioned ancestor.
   */
  strategy?: "fixed" | "absolute";
  placement?: Placement;
  offset?: number;
  gap?: number;
  hasArrow?: boolean;
  animation?: "slow" | "fast" | "none";
  buttonOnClick?: () => void;
  disabled?: boolean;
  shift?: boolean;
  /** Flip to the opposite side when there isn't enough room on the preferred side. */
  flip?: boolean;
}

/**  wrap the div you want to show the tooltip on with the Tooltip component */
const Tooltip = ({ animation = "fast", hasArrow = true, ...props }: Props) => {
  const ARROW_HEIGHT = 8;
  const GAP = props.gap ?? 0;

  const [isVisible, setIsVisible] = useState(props.hoverTooltip ? false : true);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isVisible,
    onOpenChange: setIsVisible,
    whileElementsMounted: autoUpdate,
    strategy: props.strategy ?? "absolute",
    placement: props.placement ?? "top",
    middleware: [
      props.shift && shift({ limiter: limitShift() }),
      offset({ mainAxis: ARROW_HEIGHT + GAP, crossAxis: props.offset ?? 0 }),
      props.flip && flip(),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const hover = useHover(context, {
    enabled: props.hoverTooltip ?? false,
    move: false,
  });
  const focus = useFocus(context, {
    enabled: props.hoverTooltip ?? false,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
  ]);

  const COLOR_VARIANTS = {
    light: `tw-border-blue-1 tw-bg-blue-light-1`,
    dark: "tw-border-blue-dark-1 tw-bg-blue-dark-1",
  };

  const HEADER_VARIANTS = {
    light: "tw-text-blue-1",
    dark: "tw-text-white",
  };

  const TEXT_VARIANTS = {
    light: "tw-text-gray-2",
    dark: "tw-text-gray-light-2",
  };

  const CLOSE_VARIANTS = {
    light: "tw-text-gray-4 hover:tw-text-gray-2",
    dark: "tw-text-white hover:tw-text-gray-4",
  };
  return (
    <div>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={() => props.buttonOnClick && props.buttonOnClick()}
      >
        {props.children}
      </div>
      {!props.disabled && isVisible && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            style={floatingStyles}
            className={cn(
              "tw-z-max",
              animation === "slow" && "tw-animate-fadeIn",
              animation === "fast" && "tw-animate-fadeInFast",
            )}
          >
            <div
              style={props.style}
              className={cn(
                "tw-relative tw-z-max tw-w-[185px] tw-rounded-lg tw-border-[2px] tw-border-solid tw-p-[12px] tw-shadow-md",
                props.variant
                  ? COLOR_VARIANTS[props.variant]
                  : COLOR_VARIANTS["light"],
                props.className,
              )}
            >
              {hasArrow && (
                <FloatingArrow
                  ref={arrowRef}
                  context={context}
                  height={ARROW_HEIGHT}
                  width={ARROW_HEIGHT * 2}
                  strokeWidth={1.5}
                  className={cn(
                    props.variant === "dark"
                      ? "tw-fill-blue-dark-1"
                      : "tw-fill-blue-light-1 [&>path:first-of-type]:tw-stroke-blue-1",
                  )}
                />
              )}
              <div>
                {props.header && (
                  <P1B
                    className={cn(
                      "tw-float-left tw-mb-[2px] tw-w-fit tw-font-bold tw-uppercase",
                      HEADER_VARIANTS[props.variant || "light"],
                    )}
                  >
                    {props.header}
                  </P1B>
                )}
                <div className="tw-float-right hover:tw-cursor-pointer">
                  {props.closeable && (
                    <FontAwesomeIcon
                      id="Tooltip-Close"
                      className={cn(
                        "tw-cursor-pointer tw-text-[20px]",
                        CLOSE_VARIANTS[props.variant || "light"],
                      )}
                      icon={faXmark}
                      onClick={() => {
                        setIsVisible(false);
                        props.closeFunction?.();
                      }}
                    />
                  )}
                </div>
              </div>
              <P2
                className={cn(
                  "tw-mb-0 tw-inline-block tw-text-left",
                  TEXT_VARIANTS[props.variant || "light"],
                )}
              >
                {props.text}
              </P2>
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default Tooltip;
